import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CopyToClipboard } from 'shared/components/ui/ProjectIcons';
import { copyToClipboard } from 'shared/utils/copyToClipboard';
import { CodeBuilderToggle } from 'shared/components/inputs/CodeBuilderToggle';
import {
  CODE_EDITOR_MIN_HEIGHT,
  HEADER_CLASSES,
  PANEL_CLASSES,
} from './jq-filter-builder/styles';
import { useKeyboardPick } from './jq-filter-builder/useKeyboardPick';
import { useSamplePayload } from './jq-filter-builder/useSamplePayload';
import { useJqBuilderState } from './jq-filter-builder/useJqBuilderState';
import SourcePayloadPanel from './jq-filter-builder/SourcePayloadPanel';
import JqCodeEditor from './jq-filter-builder/JqCodeEditor';
import BuilderFields from './jq-filter-builder/BuilderFields';
import OutputPreview from './jq-filter-builder/OutputPreview';

export type JqFilterBuilderProps = {
  onGenerate: (jq: string) => void;
  onCodeChange?: (jq: string) => void;
  onSampleInputChange?: (sample: object) => void;
  readOnly?: boolean;
  initialJq?: string;
  initialSampleInput?: object;
  objectType?: string;
};

// Maps a sample payload onto a Gundi object by drag-and-drop (or click/keyboard),
// generating the jq filter — or edits that filter directly in the Code view.
const JqFilterBuilder = ({
  onGenerate,
  onCodeChange,
  onSampleInputChange,
  readOnly = false,
  initialJq,
  initialSampleInput,
  objectType = 'obv',
}: JqFilterBuilderProps) => {
  const { t } = useTranslation();
  const dragPathRef = useRef<string[] | null>(null);
  const pick = useKeyboardPick();
  const payload = useSamplePayload({
    initialSampleInput,
    onSampleInputChange,
  });
  const builder = useJqBuilderState({
    initialJq,
    objectType,
    sampleInput: payload.sampleInput,
    onGenerate,
  });
  const [codeEditorHeight, setCodeEditorHeight] = useState<number>(
    CODE_EDITOR_MIN_HEIGHT,
  );

  return (
    <div className="flex flex-col">
      <div className="grid grid-cols-2 gap-3">
        <SourcePayloadPanel
          payload={payload}
          readOnly={readOnly}
          treeRef={pick.sourceTreeRef}
          onTreeEscape={pick.onTreeEscape}
          dragPathRef={dragPathRef}
          onLeafActivate={pick.activateLeaf}
          pickActive={pick.pickActive}
          armedPath={pick.sourceArmedPath}
          mappedTo={builder.mappedTo}
        />

        {/* ── Field Mappings / Code Editor Panel ── */}
        <div className={PANEL_CLASSES}>
          <div className={HEADER_CLASSES}>
            <span>{t('Field Mappings')}</span>
            <div className="flex items-center gap-2">
              {builder.isCodeMode && (
                <button
                  type="button"
                  data-testid="JqFilterBuilder-copy-button"
                  onClick={() => {
                    copyToClipboard(builder.codeText, t);
                  }}
                  className="flex-shrink-0 opacity-50 transition-opacity hover:opacity-100"
                  aria-label={t('Copy to clipboard')}
                >
                  <CopyToClipboard />
                </button>
              )}
              <CodeBuilderToggle
                isCodeMode={builder.isCodeMode}
                onToggle={builder.toggleCodeMode}
                dataTestId="JqFilterBuilder-mode-toggle"
              />
            </div>
          </div>
          {builder.isCodeMode ? (
            <JqCodeEditor
              codeText={builder.codeText}
              isComplexJq={builder.isComplexJq}
              readOnly={readOnly}
              height={codeEditorHeight}
              onHeightChange={setCodeEditorHeight}
              onChange={(text) => {
                builder.editCode(text);
                onCodeChange?.(text);
              }}
            />
          ) : (
            <BuilderFields
              builder={builder}
              sampleInput={payload.sampleInput}
              readOnly={readOnly}
              dragPathRef={dragPathRef}
              onStartPick={pick.startKeyboardPick}
              sourceArmed={pick.sourceArmedPath != null}
            />
          )}
        </div>
      </div>

      <OutputPreview
        hasMappings={builder.hasMappings}
        output={builder.mappedOutput}
        errors={builder.outputErrors}
      />
    </div>
  );
};

export default JqFilterBuilder;
